import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as Caret } from 'icons/caret-black.svg';
import CheckboxList from 'components/common/checkbox-list-component';
import ConditionalRender from 'components/util/conditional-render-component';
import {
    fetchCollapsibleCheckboxListData,
    toggleCheckboxListItem,
    toggleMasterCheckboxItem,
    resetCollapsibleCheckboxListData
} from 'store/actions/common/collapsible-checkbox-list-actions';
import PropTypes from 'prop-types';

const REMOVE_ANNOTATIONS_ID = 'batch-panel-remove-annotations';
const REMOVE_ANNOTATIONS_BLOCK = 'remove_annotations';
const EXPAND_CARET_ICON = "caret-icon--expanded";
const COLLAPSE_CARET_ICON = "caret-icon--collapsed";
const CARET_ICON_SIZE = '12px';

const CollapsibleCheckboxList = ({ title, noDataTitle, apiCall, type, shouldFetchData = true, fetchDependencies = [], dataFormatter, onMasterCheckboxChecked, onCheckboxChecked }) => {
    const [isCollapsed, setIsCollapsed] = useState(true);
    const dispatch = useDispatch();
    const checkboxList = useSelector((state) => state?.data?.collapsibleCheckboxList?.[type]);
    const hasDataBeenFetched = typeof shouldFetchData === 'function' ? shouldFetchData() : shouldFetchData;

    useEffect(() => {
        if(!isCollapsed && apiCall && type && hasDataBeenFetched && fetchDependencies?.length) {
            const withoutLoading = checkboxList?.isLoaded;
            dispatch(fetchCollapsibleCheckboxListData(type, apiCall, dataFormatter, withoutLoading));
        }
    }, [...fetchDependencies]);

    useEffect(() => {
        return () => {
            setIsCollapsed(true);
            dispatch(resetCollapsibleCheckboxListData({ type }));
        }
    }, []);

    const expandCaretIcon = () => {
        setIsCollapsed(!isCollapsed);
        if (isCollapsed && apiCall && type && hasDataBeenFetched) {
            const withoutLoading = checkboxList?.isLoaded;
            dispatch(fetchCollapsibleCheckboxListData(type, apiCall, dataFormatter, withoutLoading));
        }
    }

    const masterCheckboxToggle = (newValue) => {
        dispatch(toggleMasterCheckboxItem({ type, checked: newValue }));
        if(onMasterCheckboxChecked) {
            onMasterCheckboxChecked(newValue, type);
        }
    }

    const setChecked = (index) => {
        dispatch(toggleCheckboxListItem({ type, index }));
        if(onCheckboxChecked) {
            onCheckboxChecked(index, type);
        }
    };

    const checkboxItems = checkboxList?.checkboxItems || [];
    const checkboxListData = checkboxList || {error: null, isLoading: false, isLoaded: false};
    return (
        <div id={REMOVE_ANNOTATIONS_ID} className={REMOVE_ANNOTATIONS_BLOCK}>
            <div className={`${REMOVE_ANNOTATIONS_BLOCK}__accordian`}>
                <div className={`${REMOVE_ANNOTATIONS_BLOCK}__caret-icon`}>
                    {isCollapsed ? <Caret
                        id={COLLAPSE_CARET_ICON}
                        role={'button'}
                        className={`${EXPAND_CARET_ICON}__toggle`}
                        onClick={expandCaretIcon}
                        width={CARET_ICON_SIZE}
                        height={CARET_ICON_SIZE}
                    />
                        : <Caret
                            id={EXPAND_CARET_ICON}
                            role={'button'}
                            className={`${EXPAND_CARET_ICON}__toggle`}
                            onClick={expandCaretIcon}
                            width={CARET_ICON_SIZE}
                            height={CARET_ICON_SIZE}
                            style={{ transform: 'rotate(90deg)' }}
                        />}
                </div>
                <div className={`${REMOVE_ANNOTATIONS_BLOCK}__title`}>
                    <FormattedMessage id={title} />
                </div>
            </div>
            {!isCollapsed && hasDataBeenFetched && checkboxList && (
                <ConditionalRender dependencies={[checkboxListData]}>
                    {checkboxItems.length > 0 ? <CheckboxList
                        className={`${REMOVE_ANNOTATIONS_BLOCK}__checkbox-list`}
                        itemsList={checkboxItems}
                        setCheckbox={(index) => setChecked(index)}
                        masterCheckboxChecked={checkboxList?.masterChecked || false}
                        setMasterCheckbox={(value) =>
                            masterCheckboxToggle(value)
                        }
                        id={type}
                    /> : (
                        <div className={`${REMOVE_ANNOTATIONS_BLOCK}__no-data`}>
                            <div className={`${REMOVE_ANNOTATIONS_BLOCK}__no-data--message`}>
                                <FormattedMessage id={noDataTitle} />
                            </div>
                        </div>)}
                </ConditionalRender>)
            }
            {!isCollapsed && !hasDataBeenFetched && (
                <div className={`${REMOVE_ANNOTATIONS_BLOCK}__no-data`}>
                    <div className={`${REMOVE_ANNOTATIONS_BLOCK}__no-data--message`}>
                        <FormattedMessage id={noDataTitle} />
                    </div>
                </div>)
            }

        </div>
    );
};

CollapsibleCheckboxList.propTypes = {
    /* Title for the collapsible checkbox list */
    title: PropTypes.string.isRequired,
    /* No data title to show when there is no data */
    noDataTitle: PropTypes.string.isRequired,
    /* API call to fetch the data for the checkbox list */
    apiCall: PropTypes.func.isRequired,
    /* Type of the collapsible checkbox list */
    type: PropTypes.string.isRequired,
    /* Flag or function to determine if data should be fetched */
    shouldFetchData: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    /* Array of dependencies to watch for changes */
    fetchDependencies: PropTypes.array,
    /* Function to format the data before displaying */
    dataFormatter: PropTypes.func,
    /* Callback when master checkbox is checked/unchecked */
    onMasterCheckboxChecked: PropTypes.func,
    /* Callback when an individual checkbox is checked/unchecked */
    onCheckboxChecked: PropTypes.func
};

export default CollapsibleCheckboxList;
